/**
 * Display helpers.
 *
 * Money arrives as integer minor units and is only ever divided at the point
 * of rendering, so no arithmetic in the client can introduce a rounding
 * error into a figure a customer sees.
 */

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
})

export function money(minorUnits: number | null | undefined): string {
  return currencyFormatter.format((minorUnits ?? 0) / 100)
}

/**
 * Dollars typed by a person into integer cents.
 *
 * Rounded rather than truncated, and `null` for anything that is not a
 * number, so an empty box clears the field instead of writing zero — decision
 * 3 keeps money as integers, and this is the one place a float touches it.
 */
export function toMinorUnits(input: string): number | null {
  const trimmed = input.trim().replace(/[$,]/g, '')
  if (trimmed === '') return null

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || parsed < 0) return null

  return Math.round(parsed * 100)
}

/** Integer cents as the dollars a person would type back into a box. */
export function dollarsInput(minorUnits: number | null | undefined): string {
  if (minorUnits == null) return ''
  return (minorUnits / 100).toFixed(2)
}

const wholeDollarFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** "$7,320". For a figure read at a glance, like a year's worth; never a price. */
export function wholeDollars(minorUnits: number | null | undefined): string {
  return wholeDollarFormatter.format(Math.round((minorUnits ?? 0) / 100))
}

const dateFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-AU', {
  hour: 'numeric',
  minute: '2-digit',
})

/**
 * A date as the `yyyy-mm-dd` an `<input type="date">` wants, from its local
 * parts. Never `toISOString`: before 9:30 in Adelaide that is still
 * yesterday in UTC, and a licence that expires on the 30th must not show the
 * 29th.
 */
export function dateInputValue(value: Date | string | null | undefined): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

/** Tomorrow on this device, for a date picker's `min`. */
export function tomorrowInputValue(): string {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  return dateInputValue(date)
}

export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '—'
  return dateFormatter.format(new Date(value))
}

const dayMonthFormatter = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' })

/**
 * A date with its year only when it is not this year — "27 Sept", but
 * "27 Sept 2025". For a list cell, where the year on every row is noise.
 */
export function formatShortDate(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return '—'
  const date = new Date(value)
  return date.getFullYear() === now.getFullYear()
    ? dayMonthFormatter.format(date)
    : dateFormatter.format(date)
}

export function formatTime(value: Date | string | null | undefined): string {
  if (!value) return '—'
  return timeFormatter.format(new Date(value))
}

const shortDayFormatter = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })

/**
 * `Wed 21 Oct`. Built from parts because en-AU puts a comma after the weekday,
 * and a list of days then reads as `Wed, 21 Oct, Thu, 22 Oct`.
 */
export function formatShortDay(value: Date | string): string {
  const parts = shortDayFormatter.formatToParts(new Date(value))
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? ''
  return `${part('weekday')} ${part('day')} ${part('month')}`
}

const hourFormatter = new Intl.DateTimeFormat('en-AU', { hour: 'numeric' })

/** An hour of the day as the board's ruler writes it: `6 am`, `12 pm`. */
export function formatHour(hour: number): string {
  return hourFormatter.format(new Date(2000, 0, 1, hour))
}

/**
 * A start and an end, with am / pm written once when both share it:
 * `9:30–11:00 am`, but `11:30 am–1:00 pm`.
 */
export function formatTimeRange(start: Date | string, end: Date | string): string {
  const from = formatTime(start)
  const to = formatTime(end)
  const suffix = / [ap]m$/.exec(to)?.[0]
  return suffix && from.endsWith(suffix) ? `${from.slice(0, -suffix.length)}–${to}` : `${from}–${to}`
}

export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return `${dateFormatter.format(date)}, ${timeFormatter.format(date)}`
}

const feedDayFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
})

/** "14 Aug, 10:35 am" — the stamp the activity feed uses. Year is already
    implied by the job, so it stays off. */
export function formatFeedTime(value: Date | string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return `${feedDayFormatter.format(date)}, ${timeFormatter.format(date)}`
}

/**
 * "3 days ago", "in 2 hours".
 *
 * Relative time is right for a timeline and wrong for a due date, so screens
 * that need certainty use `formatDate` instead.
 */
export function relativeTime(value: Date | string | null | undefined): string {
  if (!value) return '—'

  const date = new Date(value)
  const seconds = Math.round((date.getTime() - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat('en-AU', { numeric: 'auto' })

  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ]

  let duration = seconds
  for (const [amount, unit] of divisions) {
    if (Math.abs(duration) < amount) return formatter.format(Math.round(duration), unit)
    duration /= amount
  }

  return formatter.format(Math.round(duration), 'year')
}

/**
 * "Marcus D". A staff name for a column too narrow to hold the whole thing.
 *
 * Surname initial rather than truncation, because the two Marcuses on the roster
 * are the case that matters and "Marcus…" tells you nothing.
 */
export function shortName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length < 2) return name
  return `${parts[0]} ${parts[1]![0]}`
}

/** "45m", "2h", "1h 12m". The shape a running clock and a day total share. */
export function formatDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes))
  const hours = Math.floor(whole / 60)
  const remainder = whole % 60

  if (hours === 0) return `${remainder}m`
  if (remainder === 0) return `${hours}h`
  return `${hours}h ${remainder}m`
}

export function durationBetween(start: Date | string, end?: Date | string | null): string {
  const from = new Date(start).getTime()
  const to = end ? new Date(end).getTime() : Date.now()
  return formatDuration((to - from) / 60_000)
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Address on one line, skipping the parts that are missing. */
export function addressLine(address: {
  unit?: string | null
  streetNumber?: string | null
  street?: string | null
  suburb?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  raw?: string | null
} | null | undefined): string {
  if (!address) return '—'

  const streetParts = [address.unit, address.streetNumber, address.street].filter(Boolean).join(' ')
  // Imported ServiceM8 locality often sits in city rather than suburb.
  const locality = address.suburb ?? address.city
  const parts = [streetParts, locality, address.state, address.postcode].filter(Boolean)

  // Falls back to whatever was imported verbatim, so a row the parser could
  // not split is still readable rather than blank.
  return parts.length > 0 ? parts.join(', ') : (address.raw ?? '—')
}

/** "1 service", "3 services", "1,016 clients"; `many` for a noun that is not just an s. */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString('en-AU')} ${count === 1 ? one : many}`
}

/** "Today", "Tomorrow", or "Fri 31 Oct": a day as a technician says it. */
export function relativeDay(value: Date | string): string {
  const date = new Date(value)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** A time today; a date and a time otherwise. For a line in a feed. */
export function feedStamp(value: Date | string): string {
  const at = new Date(value)
  return at.toDateString() === new Date().toDateString() ? formatTime(at) : formatFeedTime(at)
}
